<script setup lang="ts">
/**
 * 输入框对话框宿主。挂在 App.vue 顶层，全局只有一个。
 *
 * 与 `ConfirmHost` 分开：两者的返回类型不同（一个 void、一个 string），
 * 合并会让所有 confirm 调用点多一次没必要的类型判断。
 */
import { computed, nextTick, ref, watch } from 'vue'

import AppButton from './AppButton.vue'
import AppDialog from './AppDialog.vue'
import AppInput from './AppInput.vue'
import { usePromptState } from '@/composables/usePrompt'

const { pending, accept, dismiss } = usePromptState()

const value = ref('')
const error = ref('')
const input = ref<InstanceType<typeof AppInput> | null>(null)

const open = computed({
  get: () => pending.value !== null,
  set: (v: boolean) => {
    if (!v) dismiss()
  },
})

// 打开时把初始值放进去并聚焦 —— 重命名对话框弹出来还要用户点一下输入框，
// 是每次都会被抱怨的那种小事。
watch(pending, async (next) => {
  if (!next) return
  value.value = next.initialValue ?? ''
  error.value = ''
  await nextTick()
  input.value?.$el?.querySelector?.('input')?.focus?.()
})

function submit(): void {
  const text = value.value
  const verdict = pending.value?.validate?.(text)
  if (typeof verdict === 'string') {
    error.value = verdict
    return
  }
  accept(text)
}
</script>

<template>
  <AppDialog v-model="open" :title="pending?.title ?? ''" width="26rem">
    <p v-if="pending?.message" class="mb-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
      {{ pending.message }}
    </p>
    <AppInput
      ref="input"
      v-model="value"
      :placeholder="pending?.placeholder ?? ''"
      :invalid="!!error"
      data-test="prompt-input"
      @keyup.enter="submit"
    />
    <p v-if="error" class="mt-1.5 text-xs text-destructive" data-test="prompt-error">{{ error }}</p>

    <template #footer>
      <AppButton variant="outline" @click="dismiss">
        {{ pending?.cancelButtonText ?? '取消' }}
      </AppButton>
      <AppButton data-test="prompt-submit" @click="submit">
        {{ pending?.confirmButtonText ?? '确定' }}
      </AppButton>
    </template>
  </AppDialog>
</template>
